import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { trpc } from "@/lib/trpc";
import { 
  Users, Calendar, TrendingUp, AlertTriangle, ChevronDown, ChevronUp
} from "lucide-react";
import { useState } from "react";

const DAYS_DE = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"];

function AttendanceBar({ rate, size = "md" }: { rate: number; size?: "sm" | "md" }) {
  const getColor = (rate: number) => {
    if (rate >= 80) return "bg-green-500";
    if (rate >= 50) return "bg-yellow-500";
    return "bg-red-500";
  };
  
  const height = size === "sm" ? "h-2" : "h-3";
  
  return (
    <div className={`w-full ${height} bg-muted rounded-full overflow-hidden`}>
      <div 
        className={`${height} ${getColor(rate)} transition-all duration-300`}
        style={{ width: `${rate}%` }}
      />
    </div>
  );
}

export function AttendanceOverview() {
  const [expanded, setExpanded] = useState(false);
  const { data, isLoading } = trpc.session.getAttendanceOverview.useQuery();
  
  if (isLoading) {
    return (
      <Card className="mb-6">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-primary" />
            Anwesenheitsübersicht
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
            {[1, 2, 3, 4].map(i => (
              <Skeleton key={i} className="h-20" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }
  
  if (!data || data.courses.length === 0) {
    return null;
  }
  
  const { courses, overall } = data;
  
  // Sort courses by attendance rate (lowest first for alerts)
  const sortedCourses = [...courses].sort((a, b) => {
    // Courses with no data go to the end
    if (a.sessionsWithAttendance === 0 && b.sessionsWithAttendance === 0) return 0;
    if (a.sessionsWithAttendance === 0) return 1;
    if (b.sessionsWithAttendance === 0) return -1;
    return a.attendanceRate - b.attendanceRate;
  });
  
  // Get low attendance courses
  const lowAttendanceCourses = courses.filter(c => c.isLowAttendance);
  
  // Courses to display (limited when not expanded)
  const displayCourses = expanded ? sortedCourses : sortedCourses.slice(0, 5);
  
  return (
    <Card className="mb-6">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-primary" />
          Anwesenheitsübersicht
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Summary Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="bg-muted/50 rounded-lg p-3 text-center">
            <div className="flex items-center justify-center gap-1.5 text-muted-foreground mb-1">
              <Users className="h-4 w-4" />
              <span className="text-xs">Kurse</span>
            </div>
            <p className="text-2xl font-bold">{overall.totalCourses}</p>
          </div>
          <div className="bg-muted/50 rounded-lg p-3 text-center">
            <div className="flex items-center justify-center gap-1.5 text-muted-foreground mb-1">
              <Calendar className="h-4 w-4" />
              <span className="text-xs">Termine</span>
            </div>
            <p className="text-2xl font-bold">{overall.totalSessions}</p>
          </div>
          <div className="bg-muted/50 rounded-lg p-3 text-center">
            <div className="flex items-center justify-center gap-1.5 text-muted-foreground mb-1">
              <TrendingUp className="h-4 w-4" />
              <span className="text-xs">Ø Anwesenheit</span>
            </div>
            <p className="text-2xl font-bold">
              {overall.averageAttendance > 0 ? `${overall.averageAttendance}%` : "–"}
            </p>
          </div>
          <div className="bg-muted/50 rounded-lg p-3 text-center">
            <div className="flex items-center justify-center gap-1.5 text-muted-foreground mb-1">
              <AlertTriangle className="h-4 w-4" />
              <span className="text-xs">Niedrig</span>
            </div>
            <p className={`text-2xl font-bold ${overall.lowAttendanceCourses > 0 ? "text-red-500" : ""}`}>
              {overall.lowAttendanceCourses}
            </p>
          </div>
        </div>
        
        {/* Low Attendance Alert */}
        {lowAttendanceCourses.length > 0 && (
          <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 rounded-lg p-3">
            <div className="flex items-center gap-2 text-red-700 dark:text-red-400 mb-2">
              <AlertTriangle className="h-4 w-4" />
              <span className="font-medium text-sm">Kurse mit niedriger Anwesenheit (&lt;50%)</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {lowAttendanceCourses.map(course => (
                <Badge 
                  key={course.courseId} 
                  variant="outline" 
                  className="bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300 border-red-300 dark:border-red-700"
                >
                  {course.courseName} ({course.attendanceRate}%)
                </Badge>
              ))}
            </div>
          </div>
        )}
        
        {/* Course List */}
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-muted-foreground">Anwesenheit nach Kurs</h4>
          <div className="space-y-2">
            {displayCourses.map(course => (
              <div 
                key={course.courseId} 
                className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium text-sm truncate">{course.courseName}</span>
                    <span className="text-xs text-muted-foreground shrink-0">
                      {DAYS_DE[course.dayOfWeek]}
                    </span>
                    {course.isLowAttendance && (
                      <AlertTriangle className="h-3.5 w-3.5 text-red-500 shrink-0" />
                    )}
                  </div>
                  <AttendanceBar rate={course.attendanceRate} size="sm" />
                </div>
                <div className="text-right shrink-0 w-16">
                  <span className={`text-sm font-semibold ${
                    course.sessionsWithAttendance === 0 
                      ? "text-muted-foreground" 
                      : course.attendanceRate >= 80 
                        ? "text-green-600 dark:text-green-400" 
                        : course.attendanceRate >= 50 
                          ? "text-yellow-600 dark:text-yellow-400" 
                          : "text-red-600 dark:text-red-400"
                  }`}>
                    {course.sessionsWithAttendance > 0 ? `${course.attendanceRate}%` : "–"}
                  </span>
                  <p className="text-xs text-muted-foreground">
                    {course.sessionsWithAttendance}/{course.totalSessions}
                  </p>
                </div>
              </div>
            ))}
          </div>
          
          {/* Expand/Collapse Button */}
          {sortedCourses.length > 5 && (
            <Button 
              variant="ghost" 
              size="sm" 
              className="w-full mt-2"
              onClick={() => setExpanded(!expanded)}
            >
              {expanded ? (
                <>
                  <ChevronUp className="h-4 w-4 mr-1" />
                  Weniger anzeigen
                </>
              ) : (
                <>
                  <ChevronDown className="h-4 w-4 mr-1" />
                  Alle {sortedCourses.length} Kurse anzeigen
                </>
              )}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
